/* eslint-disable extra-rules/no-commented-out-code */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getTernaryCondition } from 'common-ui-components/lib/core/utils/common';
import Button from 'common-ui-components/dist/components/Atoms/Button';
import Anchor from 'common-ui-components/dist/components/Atoms/Anchor/Anchor';
import { USER_STATE_LOGGED_IN, SET_SIGN_IN_REDIRECT_KEY } from '../../../global/constants';
import {
  removeLastPartFromUrl,
  isQuoteFeatureEnabled,
  isEProcurementApp,
} from '../../../utils/utils';
import './OrderSummaryButtons.scss';
import { getBogoProductStatus } from '../../../utils/utilFunctions';

const handleLinkClick = (signInUrl, spPublicKey) => {
  const origin = removeLastPartFromUrl(window.location.href);
  window.open(signInUrl, '_self');
  const url = spPublicKey ? `${origin}/cart` : `${origin}/checkout`;
  window.sessionStorage.setItem(SET_SIGN_IN_REDIRECT_KEY, url);
};

const requestQuoteSignIn = signInUrl => {
  window.sessionStorage.setItem(SET_SIGN_IN_REDIRECT_KEY, window.location.href);
  window.open(signInUrl, '_self');
};

const OrderSummaryButtons = ({
  className,
  labels,
  userState,
  isMiniCart,
  signInUrl,
  checkoutButtonDisabled,
  continueShoppingUrl,
  signInButtonElementLocator,
  checkoutLinkElementLocator,
  openBlockedAccountModal,
  isAccountBlocked,
  orderSummarySubmit,
  checkoutUrl,
  createQuoteUrl,
  showContinueShopping,
  isCommerceCheckoutButtonHide,
  alreadyHaveAQuote,
  requestQuoteClick,
  isQuoteToOrderDisabled,
  userAuthentication,
  setBogoQuoteModalStatus,
  // createSetupIntentId,
  spPublicKey,
}) => {
  const [quoteIsDisabled, setQuoteIsDisabled] = useState(false);
  const isQuoteEnabled = isQuoteFeatureEnabled();
  const goToCreateQuote = flag => {
    if (flag) {
      requestQuoteClick();
      userAuthentication({ redirectToURL: createQuoteUrl });
    }
  };

  const checkAccountBlocked = (e, callback) => {
    if (isAccountBlocked) {
      openBlockedAccountModal(e);
      setQuoteIsDisabled(true);
    } else {
      callback(true);
    }
  };

  const onContinueToCheckoutClick = e => {
    const isEProcApp = isEProcurementApp();
    if (!isEProcApp) {
      userAuthentication({ redirectToURL: checkoutUrl });
    }
    orderSummarySubmit(e);
  };
  const alreadyHaveAQuoteLink = () => {
    const anchorProps = {
      className: 'row',
      elementLocator: 'link-shopping-cart-already-have-a-quote',
      to: '#',
      onClick: e =>
        userState === USER_STATE_LOGGED_IN
          ? checkAccountBlocked(e, alreadyHaveAQuote)
          : requestQuoteSignIn(signInUrl),
    };
    return (
      !(
        checkoutButtonDisabled ||
        quoteIsDisabled ||
        isCommerceCheckoutButtonHide ||
        isQuoteToOrderDisabled
      ) && (
        <div className="center-xs row already-have-a-quote">
          <Anchor {...anchorProps}>{labels.alreadyHaveAQuote}</Anchor>
        </div>
      )
    );
  };

  const createQuoteOnClick = e => {
    if (getBogoProductStatus()) {
      return setBogoQuoteModalStatus(true);
    }
    return userState === USER_STATE_LOGGED_IN
      ? checkAccountBlocked(e, goToCreateQuote)
      : requestQuoteSignIn(signInUrl);
  };

  const requestCreateQuoteCTA = () => {
    const buttonProps = {
      className: 'secondary',
      elementLocator: 'button-order-summary-request-quote',
      disabled: checkoutButtonDisabled || quoteIsDisabled,
      onClick: e => createQuoteOnClick(e),
    };
    return (
      <>
        <Button {...buttonProps}>{labels.requestAQuote}</Button>
        {alreadyHaveAQuoteLink()}
        {showContinueShopping && (
          <Anchor to={continueShoppingUrl} elementLocator="link-order-summary-continue-shopping">
            <Button className="text" elementLocator="button-order-summary-continue-shopping">
              {labels.buttonContinueShopping}
            </Button>
          </Anchor>
        )}
      </>
    );
  };

  const continueToCheckoutProps = {
    className: 'primary',
    elementLocator: signInButtonElementLocator,
    onClick: () => handleLinkClick(signInUrl, spPublicKey),
    disabled: checkoutButtonDisabled,
  };

  const anchorContinueToCheckoutProps = {
    onClick: onContinueToCheckoutClick,
    to: '#',
    className: `primary ${getTernaryCondition(checkoutButtonDisabled, 'disabled', '')}`,
    role: 'button',
  };

  return (
    <div
      className={`order-summary-buttons ${className}`}
      data-locator="order-summary-button-section"
    >
      {!isCommerceCheckoutButtonHide &&
        (userState !== USER_STATE_LOGGED_IN ? (
          <Button {...continueToCheckoutProps}>{labels.continueToCheckout}</Button>
        ) : (
          <Anchor
            {...anchorContinueToCheckoutProps}
            aria-hidden={checkoutButtonDisabled}
            tabindex={`${getTernaryCondition(checkoutButtonDisabled, '-1', '')}`}
            {...(isAccountBlocked && { onClick: e => openBlockedAccountModal(e) })}
            data-locator={checkoutLinkElementLocator}
          >
            {labels.continueToCheckout}
          </Anchor>
        ))}
      {!isEProcurementApp() && !isMiniCart && isQuoteEnabled && requestCreateQuoteCTA()}
    </div>
  );
};
OrderSummaryButtons.propTypes = {
  className: PropTypes.string,
  onRequestAQuote: PropTypes.func,
  labels: PropTypes.shape({
    signInAndCheckOut: PropTypes.string,
    requestAQuote: PropTypes.string,
    buttonContinueShopping: PropTypes.string,
    orderSummaryNote: PropTypes.string,
    continueToCheckout: PropTypes.string,
    cancel: PropTypes.string,
    alreadyHaveAQuote: PropTypes.string,
  }),
  userState: PropTypes.string.isRequired,
  isMiniCart: PropTypes.bool,
  checkoutButtonDisabled: PropTypes.bool,
  signInUrl: PropTypes.string,
  continueShoppingUrl: PropTypes.string,
  showContinueShopping: PropTypes.bool,
  signInButtonElementLocator: PropTypes.string,
  checkoutLinkElementLocator: PropTypes.string,
  openBlockedAccountModal: PropTypes.func,
  isAccountBlocked: PropTypes.bool,
  orderSummarySubmit: PropTypes.func,
  checkoutUrl: PropTypes.string,
  createQuoteUrl: PropTypes.string,
  documentIconUrl: PropTypes.string,
  isCommerceCheckoutButtonHide: PropTypes.bool,
  alreadyHaveAQuote: PropTypes.func,
  requestQuoteClick: PropTypes.func,
  isQuoteDisabled: PropTypes.bool,
  isQuoteToOrderDisabled: PropTypes.bool,
  userAuthentication: PropTypes.func,
  setBogoQuoteModalStatus: PropTypes.func,
  createSetupIntentId: PropTypes.func,
  spPublicKey: PropTypes.string,
};

OrderSummaryButtons.defaultProps = {
  onRequestAQuote: () => {
    /* default onRequestAQuote handler */
  },
  className: '',
  labels: {},
  spPublicKey: '',
  isMiniCart: false,
  checkoutButtonDisabled: false,
  signInUrl: '',
  continueShoppingUrl: '',
  signInButtonElementLocator: 'button-order-summary-sign-in-and-checkout',
  checkoutLinkElementLocator: 'link-checkout-to-continue',
  openBlockedAccountModal: () => {
    /* default openBlockedAccountModal handler */
  },
  alreadyHaveAQuote: () => {
    /* default alreadyHaveAQuote handler */
  },
  isAccountBlocked: false,
  orderSummarySubmit: () => {
    /* default orderSummarySubmit handler */
  },
  checkoutUrl: '#',
  createQuoteUrl: '#',
  documentIconUrl: '',
  showContinueShopping: false,
  isCommerceCheckoutButtonHide: false,
  requestQuoteClick: () => {
    /* default requestQuoteClick handler */
  },
  isQuoteDisabled: false,
  isQuoteToOrderDisabled: false,
  userAuthentication: () => {
    /* default userAuthentication handler */
  },
  setBogoQuoteModalStatus: () => {
    /* default setBogoQuoteModalStatus handler */
  },
  createSetupIntentId: () => {
    /* default createSetupIntentId handler */
  },
};

export default OrderSummaryButtons;
